/**
 * Ship From Types
 *
 * Types for ship-from locations (warehouses) and their addresses.
 */

// --- Enum/Union Types ---

import type { ResidentialIndicator } from './reference-data';

export type { ResidentialIndicator };

export type InventorySourceType = 'Unknown' | 'None' | 'ThirdParty' | 'Native' | 'Store' | 'FulfillmentProvider' | 'Integrations';

export type DayOfWeek = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

// --- Pickup Settings ---

export type PickupDay = {
  shipFromContactId: string | null;
  instructions: string | null;
  day: DayOfWeek;
  startTime: string | null;
  endTime: string | null;
  preferredTime: string | null;
  pickPackTime: string | null;
};

export type PickupSettings = {
  days: PickupDay[] | null;
};

// --- Ship From Contact ---

export type ShipFromContact = {
  shipFromContactId: string;
  shipFromId: string;
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  phoneNumberExtension: string | null;
  locationNotes: string | null;
};

// --- Address Types ---

/**
 * Address nested within a ship-from location (origin or return)
 */
export type ShipFromAddress = {
  name: string;
  company: string | null;
  phone: string;
  email: string;
  line1: string;
  line2: string;
  line3: string | null;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  residentialIndicator: ResidentialIndicator;
  ianaTimeZone: string | null;
};

/** Stable singleton empty address — use as a default to avoid creating new object references on every render. */
export const EMPTY_SHIP_FROM_ADDRESS: ShipFromAddress = {
  name: '',
  company: null,
  phone: '',
  email: '',
  line1: '',
  line2: '',
  line3: null,
  city: '',
  state: '',
  postalCode: '',
  countryCode: 'US',
  residentialIndicator: 'Unknown',
  ianaTimeZone: null,
};

/**
 * Ship from location (warehouse)
 */
export type ShipFrom = {
  shipFromId: string;
  isDefault: boolean;
  createDateUtc: string;
  name: string;
  originAddress: ShipFromAddress;
  returnAddress: ShipFromAddress;
  company: string | null;
  phone: string;
  line1: string;
  line2: string;
  line3: string | null;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  residentialIndicator: ResidentialIndicator;
  verificationStatus: string | null;
  verificationMessage: string | null;
  verificationUtc: string | null;
  lockAddress: boolean;
  isAssociatedToInventorySource: boolean;
  inventorySourceType: InventorySourceType;
  inventorySourceId: string | null;
  inventorySourceExternalId: string | null;
  pickupSettings: PickupSettings | null;
  fillProviderWarehouseId: string | null;
};

/**
 * Payload for creating a new ship-from location
 */
export type CreateShipFromPayload = {
  name: string;
  isDefault: boolean;
  originAddress: ShipFromAddress;
  returnAddress: ShipFromAddress;
};

/**
 * Payload for updating an existing ship-from location
 */
export type UpdateShipFromPayload = CreateShipFromPayload & {
  shipFromId: string;
};
